import type { CSSProperties } from 'react';
import styles from './Spinner.module.css';

export interface SpinnerProps {
  /** Diameter in px (Material default 36). */
  size?: number;
  /** Stroke width (Material default 4). */
  strokeWidth?: number;
  /** Arc colour; defaults to the theme primary. */
  color?: string;
  /** Optional track colour behind the arc. */
  trackColor?: string;
  /** 0..1 for a determinate arc; omit for the indeterminate spinner. */
  value?: number;
  /** Accessible label; decorative when omitted. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

/** Material CircularProgressIndicator. */
export function Spinner({ size = 36, strokeWidth = 4, color, trackColor, value, label, className, style }: SpinnerProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const determinate = value !== undefined;
  const clamped = Math.min(1, Math.max(0, value ?? 0));
  return (
    <span
      className={[styles.spinner, determinate ? '' : styles.indeterminate, className ?? ''].filter(Boolean).join(' ')}
      style={{ width: size, height: size, color: color ?? 'var(--color-primary)', ...style }}
      role="progressbar"
      aria-label={label}
      aria-hidden={label ? undefined : true}
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? 100 : undefined}
      aria-valuenow={determinate ? Math.round(clamped * 100) : undefined}
      data-motion-essential=""
    >
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        {trackColor && (
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
        )}
        <circle
          className={styles.arc}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={determinate ? circumference * (1 - clamped) : undefined}
          style={{ '--circumference': `${circumference}` } as CSSProperties}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
    </span>
  );
}
